// src/lib/demoData.js
// Rich, YC-demo-grade fake data. Nothing is real. Patient names are generated.
// CARC/RARC codes and CPT codes are real but attached to fictional encounters.

export const TENANT = {
  name: "Stanford Healthcare · Demo Tenant",
  shortName: "stanfordhealth",
  npi: "1769XXXXXX",
  ehr: "Epic",
  goLive: "2026-04-02",
};

export const USER = {
  name: "Dr. Jason Morrow",
  email: "reviewer@JasonMorrow.com",
  role: "Revenue Cycle Leader",
  initials: "JM",
};

export const STATS = {
  recoveredYTD: 2_184_300,
  recoveredTrend: +0.37,
  openDenials: 127,
  overturnRate: 0.62,
  overturnTrend: +0.08,
  avgTurnaroundHours: 9.4,
  autoSubmitPercent: 0.41,
};

export const EOB_SNIPPET = `REMIT 835 · 2026-04-15
Claim ID:  CH-00293418
Patient:   HARRIS, J. (MRN 8821)
Payer:     Anthem Blue Cross Blue Shield
Service:   CPT 29881 · Arthroscopic knee meniscus repair w/ chondroplasty
Charged:   $8,490.00
Allowed:   $0.00
Paid:      $0.00
Denied:    $4,210.00
CARC-197:  Precertification / authorization absent.
RARC-N517: Submit missing prior authorization.`;

export const APPEAL_LETTER = `Dear Anthem Blue Cross Blue Shield Appeals Team,

We respectfully request formal reconsideration of claim CH-00293418, denied under CARC-197 ("Precertification/authorization absent") for patient J.H. (MRN 8821), for an arthroscopic medial meniscus repair with chondroplasty (CPT 29881) of the left knee performed on 2026-03-18 by Dr. L. Nakamura, MD.

We believe the denial should be overturned for the reasons below.

1. Prior authorization was not required for this presentation. Under Anthem's 2026 commercial medical policy SURG.00087 §3.4, prior authorization is waived when the patient presents with an acute meniscal tear with a confirmed mechanical symptom (locking, catching, or giving way) that has failed at least six weeks of documented conservative care. The attached MRI (2026-03-15) demonstrates a displaced bucket-handle tear of the medial meniscus with clinical mechanical symptoms documented on the same visit.

2. Medical necessity is well documented. The attached operative report (2026-03-18) and preoperative history confirm persistent left knee pain ≥ 8 months with mechanical locking events, physical therapy × 10 sessions without durable relief, and radiographic evidence of tricompartmental osteoarthritis with medial joint space narrowing. These findings meet Medicare LCD L33410 criteria for arthroscopic knee surgery under CPT 29881.

3. The service was delivered in-network by a participating provider. Dr. Nakamura is a credentialed Anthem Tier-1 orthopedic surgeon. The facility (Stanford Orthopedic Center) is an in-network ambulatory surgery center.

Attached to this appeal:
    • Operative report (2026-03-18, Dr. L. Nakamura)
    • Left knee MRI, radiologist's report (2026-03-15)
    • Left knee X-ray, AP + lateral (2026-02-01)
    • Pre-op physical therapy notes (2026-01-02 through 2026-03-10)
    • Anthem SURG.00087 policy excerpt
    • Medicare LCD L33410 excerpt
    • Provider + facility in-network verification

Please reverse the denial and reprocess claim CH-00293418 for payment in the amount of $4,210.00 at the contracted rate. Under Anthem's internal review policy, we expect a written determination within 30 days.

Respectfully,

Revenue Cycle Department
Stanford Healthcare
Submitted on behalf of Dr. L. Nakamura, MD
Generated by PanelZero · Draft ID pz-apl-7F4C · Evidence pack sha256:1a3d…c8`;

export const CLINICAL_NARRATIVE = `CLINICAL JUSTIFICATION — CPT 29881 · Arthroscopic meniscus repair with chondroplasty, left knee

Patient presented with an 8-month history of progressive left knee pain with recurrent mechanical locking and episodes of giving way, worst on stair descent and after prolonged standing. A structured course of physical therapy (10 sessions over 10 weeks, 2026-01-02 through 2026-03-10) produced no durable improvement in pain scores or functional testing. Patient reported inability to perform work duties as a warehouse team lead.

On examination the patient demonstrated positive McMurray and Thessaly tests on the left, tenderness localized to the medial joint line, and a mild antalgic gait. Range of motion was reduced to 5-115 degrees with catching at terminal extension.

Weight-bearing AP and lateral radiographs of the left knee (2026-02-01) demonstrated medial compartment joint-space narrowing, subchondral sclerosis, and marginal osteophytes consistent with Kellgren-Lawrence grade 3 osteoarthritis. MRI obtained 2026-03-15 (PD TSE fat-saturated sagittal sequence) demonstrated a displaced bucket-handle tear of the posterior horn of the medial meniscus with a flipped fragment into the intercondylar notch, full-thickness chondral fissuring of the medial femoral condyle, and a small joint effusion.

Conservative management options had been exhausted. Arthroscopic partial meniscectomy with chondroplasty (CPT 29881) was indicated to address the mechanical derangement and relieve locking symptoms.

Repair was performed 2026-03-18 with partial medial meniscectomy of the bucket-handle fragment and chondroplasty of the medial femoral condyle. Intraoperative findings matched the MRI. No complications. Post-op rehabilitation protocol initiated per standard Stanford Orthopedic pathway.

Per Medicare LCD L33410 and Anthem policy SURG.00087, the clinical picture — displaced meniscal tear with persistent mechanical symptoms, failed conservative care, and confirming imaging — meets every listed indication for arthroscopic meniscus surgery.`;

export const OP_NOTE_EXCERPT = `OPERATIVE REPORT — excerpt
Date:         2026-03-18
Surgeon:      L. Nakamura, MD
Anesthesia:   Spinal + MAC sedation
Procedure:    Arthroscopic partial medial meniscectomy with chondroplasty,
              left knee (CPT 29881)
Diagnosis:    Displaced bucket-handle tear, medial meniscus (M23.205);
              osteoarthritis, left knee (M17.12)

FINDINGS
  • Displaced bucket-handle tear, posterior horn of medial meniscus
    with flipped fragment into the intercondylar notch
  • Full-thickness chondral fissuring (Outerbridge IV) of the medial
    femoral condyle, ~1.5 cm², with surrounding grade II change
  • Intact ACL, PCL, lateral meniscus; mild synovitis

PROCEDURE (summary)
  1. Standard anteromedial / anterolateral portals established.
  2. Diagnostic arthroscopy of all compartments.
  3. Reduction and trimming of the flipped meniscal fragment; partial
     medial meniscectomy back to a stable peripheral rim (~40%).
  4. Chondroplasty of the medial femoral condyle using a mechanical
     shaver; loose flaps removed, base stabilized.
  5. Copious irrigation; arthroscopic inspection confirmed no retained
     fragments. Portals closed in standard fashion.

EBL: < 20 mL    Complications: none    Disposition: PACU, stable.`;

export const LCD_CITATION = `MEDICARE LCD L33410 — Arthroscopy of the Knee (excerpt)
§ Covered indications for arthroscopic meniscus surgery include ALL of:
  (a) Displaced or unstable meniscal tear confirmed by MRI
  (b) Persistent mechanical symptoms (locking, catching, giving way)
  (c) Failure of at least 6 weeks of documented conservative care
  (d) No uncorrected contraindications to arthroscopic surgery

ANTHEM MEDICAL POLICY SURG.00087 · 2026.01 (excerpt)
§ 3.4 Prior authorization is NOT required when all of the following apply:
  (i)   Displaced meniscal tear confirmed on imaging within 60 days
  (ii)  Documented mechanical symptoms (locking, catching, instability)
  (iii) In-network facility and credentialed surgeon
  (iv)  Documented failure of ≥ 6 weeks of appropriate conservative care

This case satisfies every clause of both policies (see attached records).`;

export const ATTACHMENT_META = [
  { name: "appeal_letter.pdf", size: "62 KB", kind: "letter" },
  { name: "clinical_justification.pdf", size: "48 KB", kind: "narrative" },
  { name: "op_note_2026-03-18.pdf", size: "31 KB", kind: "operative" },
  { name: "mri_left_knee_2026-03-15.pdf", size: "221 KB", kind: "imaging" },
  { name: "xray_left_knee_2026-02-01.pdf", size: "184 KB", kind: "imaging" },
  { name: "pt_notes_2026-01-02_to_03-10.pdf", size: "132 KB", kind: "therapy" },
  { name: "anthem_SURG.00087.pdf", size: "19 KB", kind: "policy" },
  { name: "medicare_LCD_L33410.pdf", size: "22 KB", kind: "policy" },
];

export const ACTIVITY_FEED = [
  { at: "02:14 ago", who: "PanelZero", what: "Drafted appeal for denial CO-197 · $4,210" },
  { at: "08:41 ago", who: "PanelZero", what: "Pulled 72 new denials from clearinghouse 835 feed" },
  { at: "17:02 ago", who: "J. Romero", what: "Reviewed and approved 6 appeals" },
  { at: "1d ago",    who: "Anthem",    what: "Overturned denial CO-16 · $3,180 recovered" },
  { at: "1d ago",    who: "PanelZero", what: "Auto-submitted 14 appeals (high-confidence)" },
  { at: "2d ago",    who: "UHC",       what: "Overturned N115 · $2,840 recovered" },
];

export const DENIALS = [
  {
    id: "D-8821",
    featured: true,
    patient: { initials: "J.H.", mrn: "8821", name: "HARRIS, J.", dob: "1978-05-12", sex: "M" },
    encounter: "2026-03-18",
    payer: "Anthem BCBS",
    plan: "Commercial PPO",
    claimId: "CH-00293418",
    cpt: "29881",
    dx: "M23.205",
    service: "Arthroscopic meniscus repair w/ chondroplasty, L knee",
    charged: 8490,
    allowed: 0,
    amount: 4210,
    carc: "CO-197",
    carcLabel: "Precertification / authorization absent",
    rarc: "N517",
    aiScore: 0.94,
    winProbability: 0.92,
    reversible: true,
    status: "drafted",
    action: "Submit via portal",
    receivedAt: "2026-04-15 08:02",
    assignee: "PanelZero · auto-draft",
    reasonShort: "Prior auth claimed missing — policy waives it for displaced meniscal tears with mechanical symptoms.",
  },
  {
    id: "D-8822",
    patient: { initials: "M.S.", mrn: "8822", name: "SANTOS, M.", dob: "1965-09-21", sex: "F" },
    encounter: "2026-03-22",
    payer: "UnitedHealthcare",
    plan: "Medicare Advantage",
    claimId: "CH-00293419",
    cpt: "97110",
    dx: "M54.5",
    service: "Physical therapy · therapeutic exercise",
    charged: 1480,
    allowed: 300,
    amount: 1180,
    carc: "CO-16",
    carcLabel: "Claim lacks information / has submission error",
    rarc: "M119",
    aiScore: 0.89,
    winProbability: 0.81,
    reversible: true,
    status: "submitted",
    action: "Tracking",
    receivedAt: "2026-04-14 09:11",
    assignee: "PanelZero · auto-submitted",
    reasonShort: "Missing progress note pages 3–4; evidence attached on resubmission.",
  },
  {
    id: "D-8823",
    patient: { initials: "D.T.", mrn: "8823", name: "TANAKA, D.", dob: "1982-02-14", sex: "M" },
    encounter: "2026-03-26",
    payer: "Aetna",
    plan: "Commercial HMO",
    claimId: "CH-00293420",
    cpt: "95810",
    dx: "G47.33",
    service: "Polysomnography · full night",
    charged: 3190,
    allowed: 0,
    amount: 2840,
    carc: "N115",
    carcLabel: "Decision based on LCD / policy",
    rarc: "N386",
    aiScore: 0.76,
    winProbability: 0.68,
    reversible: true,
    status: "overturned",
    action: "Recovered",
    receivedAt: "2026-04-11 14:26",
    assignee: "J. Romero",
    reasonShort: "Meets Aetna OHCM §3.1 indication for PSG (AHI ≥ 15 documented).",
  },
  {
    id: "D-8824",
    patient: { initials: "A.K.", mrn: "8824", name: "KHAN, A.", dob: "1974-11-02", sex: "F" },
    encounter: "2026-03-29",
    payer: "Cigna",
    plan: "Commercial POS",
    claimId: "CH-00293421",
    cpt: "22612",
    dx: "M48.061",
    service: "Lumbar spinal fusion, single level",
    charged: 41800,
    allowed: 0,
    amount: 9620,
    carc: "CO-50",
    carcLabel: "Service deemed not medically necessary",
    rarc: "M127",
    aiScore: 0.71,
    winProbability: 0.58,
    reversible: true,
    status: "queued",
    action: "Draft",
    receivedAt: "2026-04-15 07:48",
    assignee: "Pending triage",
    reasonShort: "Peer-to-peer needed; neurodeficit + failed 12-wk conservative care.",
  },
  {
    id: "D-8825",
    patient: { initials: "T.H.", mrn: "8825", name: "HUGHES, T.", dob: "1960-07-30", sex: "M" },
    encounter: "2026-02-10",
    payer: "BCBS Massachusetts",
    plan: "Medicare Supplement",
    claimId: "CH-00293422",
    cpt: "99213",
    dx: "I10",
    service: "Office visit · established patient",
    charged: 190,
    allowed: 0,
    amount: 560,
    carc: "CO-29",
    carcLabel: "Time limit for filing has expired",
    rarc: "N30",
    aiScore: 0.42,
    winProbability: 0.29,
    reversible: false,
    status: "closed",
    action: "Write off",
    receivedAt: "2026-04-09 12:05",
    assignee: "R. Ellis",
    reasonShort: "Timely filing expired; attempted good-cause waiver — declined.",
  },
  {
    id: "D-8826",
    patient: { initials: "E.P.", mrn: "8826", name: "PETERSON, E.", dob: "1991-04-18", sex: "F" },
    encounter: "2026-03-31",
    payer: "Humana",
    plan: "Medicare Advantage",
    claimId: "CH-00293423",
    cpt: "72148",
    dx: "M51.26",
    service: "MRI lumbar, without contrast",
    charged: 3810,
    allowed: 470,
    amount: 3340,
    carc: "PR-204",
    carcLabel: "Service not covered under current benefit plan",
    rarc: "N130",
    aiScore: 0.83,
    winProbability: 0.76,
    reversible: true,
    status: "drafted",
    action: "Review",
    receivedAt: "2026-04-15 06:55",
    assignee: "PanelZero · auto-draft",
    reasonShort: "Correct benefit bucket is diagnostic imaging — plan excerpt attached.",
  },
  {
    id: "D-8827",
    patient: { initials: "R.M.", mrn: "8827", name: "MARTINEZ, R.", dob: "1959-08-03", sex: "M" },
    encounter: "2026-03-03",
    payer: "Anthem BCBS",
    plan: "Commercial PPO",
    claimId: "CH-00293424",
    cpt: "93458",
    dx: "I25.110",
    service: "Diagnostic cardiac catheterization",
    charged: 14600,
    allowed: 0,
    amount: 6780,
    carc: "CO-151",
    carcLabel: "Payment adjusted based on payer's clinical review",
    rarc: "N388",
    aiScore: 0.79,
    winProbability: 0.70,
    reversible: true,
    status: "drafted",
    action: "Review",
    receivedAt: "2026-04-14 21:40",
    assignee: "PanelZero · auto-draft",
    reasonShort: "Positive stress echo documented; meets AHA/ACC appropriateness criteria.",
  },
  {
    id: "D-8828",
    patient: { initials: "L.R.", mrn: "8828", name: "ROSSI, L.", dob: "1986-12-24", sex: "F" },
    encounter: "2026-04-01",
    payer: "Kaiser Permanente",
    plan: "Commercial HMO",
    claimId: "CH-00293425",
    cpt: "43239",
    dx: "K21.9",
    service: "EGD with biopsy",
    charged: 2740,
    allowed: 210,
    amount: 1990,
    carc: "N10",
    carcLabel: "Adjustment based on clinical review",
    rarc: "N193",
    aiScore: 0.65,
    winProbability: 0.55,
    reversible: true,
    status: "queued",
    action: "Draft",
    receivedAt: "2026-04-15 02:13",
    assignee: "Pending triage",
    reasonShort: "Chronic GERD + alarm features support upper endoscopy per ACG 2022.",
  },
];

// status → display metadata
export const STATUS_META = {
  queued:     { label: "Queued",       tone: "grey",   dot: "#9aa3b2" },
  drafted:    { label: "Draft ready",  tone: "teal",   dot: "#14b8b8" },
  submitted:  { label: "Submitted",    tone: "blue",   dot: "#2563eb" },
  overturned: { label: "Overturned",   tone: "green",  dot: "#10b981" },
  closed:     { label: "Closed · WO",  tone: "red",    dot: "#ef4444" },
};

export const FILTERS = [
  { id: "all", label: "All denials" },
  { id: "drafted", label: "Ready to review" },
  { id: "submitted", label: "In flight" },
  { id: "overturned", label: "Overturned" },
  { id: "queued", label: "Needs triage" },
];

export const DEMO_CREDS = {
  username: (process.env.REACT_APP_DEMO_USER || "yc").trim().toLowerCase(),
  password: process.env.REACT_APP_DEMO_PASS || "panelzero2026",
};

/* ──────────────────────────────────────────────────────────────
   Voice calls — Vapi-style call records
   ────────────────────────────────────────────────────────────── */
export const VOICE_CALLS = [
  {
    id: "vc_8a3f24b6",
    denialId: "D-8821",
    claimId: "CH-00293418",
    payer: "Anthem BCBS",
    phone: "+18008524478",
    purpose: "Prior auth retro-review · CPT 29881",
    status: "completed",
    durationSeconds: 412,
    disclosedAi: true,
    startedAt: "2026-04-17 10:18",
    endedAt: "2026-04-17 10:25",
    outcome: "Reference # received · case escalated to medical review",
    referenceNumber: "ANT-4491-2026",
    recordingUrl: "/demo/voice-demo.mp3",
    aiSummary:
      "Called Anthem prior-auth line. Navigated IVR in 47s. Disclosed AI agent per policy. Spoke to rep Monica, opened retro-review case under claim CH-00293418. Provided CPT, DOS, CARC and requested expedited medical-director review. Rep assigned reference ANT-4491-2026 and confirmed 10-day turnaround.",
    functionCalls: [
      { fn: "lookup_claim_status", args: { claimId: "CH-00293418" }, result: { status: "denied", carc: "CO-197" } },
      { fn: "record_reference_number", args: { reference: "ANT-4491-2026" } },
    ],
    transcript: `PZ-Agent: Hi, this is PanelZero calling on behalf of Stanford Healthcare regarding claim number CH-00293418. I want to disclose up front that I'm an AI-assisted agent and this call is being recorded for quality.
Anthem rep: Thanks, what can I help with?
PZ-Agent: I'd like to open a retroactive prior-authorization review for patient Harris. The service was an arthroscopic meniscus repair, CPT 29881, performed 2026-03-18 by Dr. Nakamura. The claim was denied under CARC-197.
Anthem rep: One moment. Okay, I see the claim. What's the basis of the appeal?
PZ-Agent: Policy SURG.00087 section 3.4 waives prior auth for displaced meniscal tears with documented mechanical symptoms and failed conservative care. We have imaging, operative report, and ten sessions of PT supporting that.
Anthem rep: Understood. I'll route this to medical review. Your reference number is A-N-T dash four four nine one dash two zero two six.
PZ-Agent: Thank you. I've recorded reference ANT-4491-2026. What's the expected turnaround?
Anthem rep: Ten business days. You'll get a written determination.
PZ-Agent: Perfect. Have a good day.`,
  },
  {
    id: "vc_5c8e1217",
    denialId: "D-8824",
    claimId: "CH-00293421",
    payer: "Cigna",
    phone: "+18772447247",
    purpose: "P2P scheduling",
    status: "in_progress",
    durationSeconds: 174,
    disclosedAi: true,
    startedAt: "2026-04-18 09:41",
    endedAt: null,
    outcome: "On hold, queued for medical director",
  },
  {
    id: "vc_22bd0044",
    denialId: "D-8826",
    claimId: "CH-00293423",
    payer: "Humana",
    phone: "+18004576334",
    purpose: "Fax confirmation follow-up",
    status: "voicemail",
    durationSeconds: 52,
    disclosedAi: true,
    startedAt: "2026-04-18 08:12",
    endedAt: "2026-04-18 08:13",
    outcome: "Left voicemail requesting fax confirmation for A-48201",
  },
  {
    id: "vc_91e4cd33",
    denialId: "D-8823",
    claimId: "CH-00293420",
    payer: "Aetna",
    phone: "+18888825685",
    purpose: "Claim status inquiry",
    status: "completed",
    durationSeconds: 238,
    disclosedAi: true,
    startedAt: "2026-04-17 15:02",
    endedAt: "2026-04-17 15:06",
    outcome: "Denial overturned · payment scheduled 2026-04-22",
    referenceNumber: "AET-76221",
  },
  {
    id: "vc_0dd1a9b8",
    denialId: "D-8827",
    claimId: "CH-00293424",
    payer: "Anthem BCBS",
    phone: "+18008524478",
    purpose: "Cardiac cath peer-to-peer",
    status: "no_answer",
    durationSeconds: 0,
    disclosedAi: true,
    startedAt: "2026-04-16 16:41",
    endedAt: "2026-04-16 16:42",
    outcome: "No answer · retrying in 4h",
  },
  {
    id: "vc_0a1f74ce",
    denialId: "D-8822",
    claimId: "CH-00293419",
    payer: "UnitedHealthcare",
    phone: "+18666336411",
    purpose: "Missing-docs clarification",
    status: "completed",
    durationSeconds: 96,
    disclosedAi: true,
    startedAt: "2026-04-16 11:30",
    endedAt: "2026-04-16 11:32",
    outcome: "Rep confirmed receipt, reprocessing started",
  },
  {
    id: "vc_fe30551a",
    denialId: "D-8828",
    claimId: "CH-00293425",
    payer: "Kaiser Permanente",
    phone: "+18004647001",
    purpose: "Submit corrected 837",
    status: "failed",
    durationSeconds: 0,
    disclosedAi: true,
    startedAt: "2026-04-18 06:10",
    endedAt: "2026-04-18 06:10",
    outcome: "Carrier rejected call · retry with direct line",
  },
  {
    id: "vc_1c822046",
    denialId: null,
    claimId: null,
    payer: "Anthem BCBS",
    phone: "+18008524478",
    purpose: "Monthly payer-hours discovery",
    status: "queued",
    durationSeconds: 0,
    disclosedAi: true,
    startedAt: null,
    endedAt: null,
    outcome: "Scheduled for 2026-04-19 08:00",
  },
];

export const VOICE_STATUS_META = {
  queued:      { tone: "grey",   label: "Queued" },
  ringing:     { tone: "amber",  label: "Ringing" },
  in_progress: { tone: "blue",   label: "In progress" },
  completed:   { tone: "green",  label: "Completed" },
  failed:      { tone: "red",    label: "Failed" },
  no_answer:   { tone: "amber",  label: "No answer" },
  voicemail:   { tone: "purple", label: "Voicemail" },
};

/* ──────────────────────────────────────────────────────────────
   Faxes — SRFax-style records
   ────────────────────────────────────────────────────────────── */
export const FAXES = [
  {
    id: "fx_aad10224",
    appealId: "apl_7F4C-293418",
    denialId: "D-8821",
    direction: "outbound",
    to: "+18779112219",
    from: "+16504984488",
    toLabel: "Anthem BCBS · Appeals fax",
    status: "delivered",
    pages: 7,
    attempts: 1,
    maxAttempts: 3,
    confirmation: "SRFAX-88412-2026",
    sentAt: "2026-04-16 11:24",
    deliveredAt: "2026-04-16 11:27",
    documentName: "appeal_package_CH-00293418.pdf",
    lastError: null,
  },
  {
    id: "fx_40d781fa",
    appealId: "apl_8B29-293423",
    denialId: "D-8826",
    direction: "outbound",
    to: "+18005548725",
    from: "+16504984488",
    toLabel: "Humana · Appeals fax",
    status: "sending",
    pages: 6,
    attempts: 2,
    maxAttempts: 3,
    confirmation: null,
    sentAt: "2026-04-18 08:02",
    deliveredAt: null,
    documentName: "appeal_package_CH-00293423.pdf",
    lastError: null,
  },
  {
    id: "fx_2261a300",
    appealId: "apl_4D12-293419",
    denialId: "D-8822",
    direction: "outbound",
    to: "+18884821704",
    from: "+16504984488",
    toLabel: "UHC · Medical appeals",
    status: "delivered",
    pages: 5,
    attempts: 1,
    maxAttempts: 3,
    confirmation: "SRFAX-88301-2026",
    sentAt: "2026-04-14 14:15",
    deliveredAt: "2026-04-14 14:18",
    documentName: "appeal_package_CH-00293419.pdf",
  },
  {
    id: "fx_15fa77e0",
    appealId: "apl_11A3-293420",
    denialId: "D-8823",
    direction: "outbound",
    to: "+18882821925",
    from: "+16504984488",
    toLabel: "Aetna · Appeals fax",
    status: "delivered",
    pages: 6,
    attempts: 1,
    maxAttempts: 3,
    confirmation: "SRFAX-88198-2026",
    sentAt: "2026-04-11 15:40",
    deliveredAt: "2026-04-11 15:43",
    documentName: "appeal_package_CH-00293420.pdf",
  },
  {
    id: "fx_72c09c11",
    appealId: "apl_9C83-293424",
    denialId: "D-8827",
    direction: "outbound",
    to: "+18779112219",
    from: "+16504984488",
    toLabel: "Anthem BCBS · Appeals fax",
    status: "failed",
    pages: 9,
    attempts: 3,
    maxAttempts: 3,
    confirmation: null,
    sentAt: "2026-04-15 22:41",
    deliveredAt: null,
    documentName: "appeal_package_CH-00293424.pdf",
    lastError: "NO_ANSWER after 3 attempts. Remote line did not pick up within 45s ring.",
  },
  {
    id: "fx_b88ff231",
    appealId: null,
    denialId: "D-8828",
    direction: "outbound",
    to: "+18884219711",
    from: "+16504984488",
    toLabel: "Kaiser · Clinical review fax",
    status: "queued",
    pages: 5,
    attempts: 0,
    maxAttempts: 3,
    confirmation: null,
    sentAt: null,
    deliveredAt: null,
    documentName: "appeal_package_CH-00293425.pdf",
  },
  {
    id: "fx_5de81403",
    appealId: null,
    denialId: null,
    direction: "inbound",
    to: "+16504984488",
    from: "+18779112219",
    toLabel: "From Anthem BCBS",
    status: "delivered",
    pages: 2,
    attempts: 1,
    maxAttempts: 1,
    confirmation: "SRFAX-RX-22011",
    sentAt: "2026-04-17 09:10",
    deliveredAt: "2026-04-17 09:10",
    documentName: "anthem_determination_response.pdf",
  },
  {
    id: "fx_9cc0a104",
    appealId: "apl_1F4A-293421",
    denialId: "D-8824",
    direction: "outbound",
    to: "+18006332200",
    from: "+16504984488",
    toLabel: "Cigna · Appeals fax",
    status: "partial",
    pages: 11,
    attempts: 2,
    maxAttempts: 3,
    confirmation: "SRFAX-88511-2026",
    sentAt: "2026-04-17 18:01",
    deliveredAt: "2026-04-17 18:06",
    documentName: "appeal_package_CH-00293421.pdf",
    lastError: "Pages 9-11 marked as partial; auto-retry queued for 2026-04-18 06:00.",
  },
];

export const FAX_STATUS_META = {
  queued:    { tone: "grey",  label: "Queued" },
  sending:   { tone: "blue",  label: "Sending" },
  sent:      { tone: "blue",  label: "Sent" },
  delivered: { tone: "green", label: "Delivered" },
  failed:    { tone: "red",   label: "Failed" },
  partial:   { tone: "amber", label: "Partial" },
};

/* ──────────────────────────────────────────────────────────────
   Peer-to-peer review recommendations
   ────────────────────────────────────────────────────────────── */
export const P2P = {
  "D-8821": {
    recommended: true,
    winWithout: 0.64,
    winWith:    0.89,
    medicalDirector: "Dr. R. Ortega, Anthem Regional MD (Orthopedics)",
    nextAvailable: "Apr 17 · 10:15 AM PT",
    durationMin: 15,
    talkingPoints: [
      "Anthem SURG.00087 §3.4 — prior auth is waived for displaced meniscal tear with confirmed mechanical symptoms after failed conservative care. This case meets every clause.",
      "MRI 2026-03-15 shows a displaced bucket-handle tear of the medial meniscus with a flipped fragment — lead with the imaging.",
      "Failed 10 sessions of PT (notes attached). Positive McMurray and Thessaly; mechanical locking documented.",
      "Left knee X-ray 2026-02-01: K-L grade 3 OA with medial joint-space narrowing — the mechanical symptoms are tear-driven, not simple OA.",
      "In-network: Dr. Nakamura is Anthem Tier-1; Stanford Orthopedic Center is in-network.",
    ],
    evidence: [
      { label: "MRI",      pageRef: 5 },
      { label: "X-ray",    pageRef: 5 },
      { label: "Op note",  pageRef: 6 },
      { label: "Policy",   pageRef: 7 },
    ],
  },
  "D-8824": {
    recommended: true,
    winWithout: 0.58,
    winWith:    0.80,
    medicalDirector: "Dr. S. Iyer, Cigna Regional MD (Spine)",
    nextAvailable: "Apr 18 · 2:00 PM PT",
    durationMin: 20,
    talkingPoints: [
      "12 weeks of documented conservative care (PT, ESI × 2, pharmacologic) with no durable relief.",
      "New L4-L5 motor deficit (EHL 3/5) and progressive claudication.",
      "MRI shows grade II spondylolisthesis with foraminal stenosis — meets Cigna CPB 0144 surgical criteria.",
      "Offer peer-reviewed literature on fusion outcomes for this indication if needed.",
    ],
    evidence: [
      { label: "MRI lumbar",       pageRef: 3 },
      { label: "EMG report",       pageRef: 4 },
      { label: "PT / ESI records", pageRef: 5 },
    ],
  },
};

/* ──────────────────────────────────────────────────────────────
   Imaging studies (DICOM-style metadata; stylized SVG slices
   rendered in the UI from these descriptors)
   ────────────────────────────────────────────────────────────── */
/*
 * Imaging uses real Wikimedia Commons photographs redistributed under CC licenses.
 * See public/demo/ATTRIBUTION.md for original authors and license links.
 */
export const IMAGING = {
  "D-8821": {
    studyDate: "2026-03-15",
    accession: "RAD-00841-26",
    modality: "MR",
    bodyPart: "LEFT KNEE",
    radiologist: "K. Patel, MD",
    radiologistTitle: "Board-Certified Radiologist · Stanford Radiology",
    facility: "Stanford Radiology · MSK Division",
    department: "Musculoskeletal Imaging",
    finding: "Displaced bucket-handle tear, medial meniscus, posterior horn",
    impression:
      "Displaced bucket-handle tear of the posterior horn of the medial meniscus with a flipped fragment in the intercondylar notch. Full-thickness chondral fissuring of the medial femoral condyle (Outerbridge IV, ~1.5 cm²). Small joint effusion. Medial compartment narrowing consistent with Kellgren-Lawrence grade 3 osteoarthritis. ACL/PCL/lateral meniscus intact.",
    indication: "8-month left knee pain with mechanical locking; failed conservative care.",
    technique: "PD TSE fat-saturated sagittal + coronal + axial on a 1.5T system; routine knee MR protocol without contrast.",
    comparison: "Prior radiographs 2026-02-01 show medial compartment osteoarthritis.",
    findings: [
      "Posterior horn, medial meniscus: displaced bucket-handle tear with flipped fragment (double-PCL sign) visible in the intercondylar notch.",
      "Medial femoral condyle: full-thickness chondral fissure measuring ~1.5 cm² with surrounding grade II change.",
      "Medial compartment: joint-space narrowing with subchondral sclerosis and marginal osteophytes.",
      "Lateral meniscus, ACL, PCL, MCL, LCL: intact.",
      "Small simple joint effusion. No Baker cyst.",
    ],
    series: [
      {
        id: "s1",
        view: "Sagittal PD TSE FS",
        slice: "12/24",
        orientationL: "A",
        orientationR: "P",
        image: "/demo/knee-mri-sagittal.jpg",
        modalityTag: "MR",
        credit: "Image: Ptrump16, CC BY-SA 4.0",
        creditUrl: "https://commons.wikimedia.org/wiki/File:Knee_MRI_PD_TSE_FS_Sagittal.jpg",
      },
      {
        id: "s2",
        view: "AP Left Knee XR",
        slice: "1/2",
        orientationL: "L",
        orientationR: "R",
        image: "/demo/knee-xray-oa.jpg",
        modalityTag: "XR",
        credit: "Image: James Heilman, MD, CC BY-SA 3.0",
        creditUrl: "https://commons.wikimedia.org/wiki/File:Osteoarthritis_left_knee.jpg",
      },
    ],
  },
  "D-8824": {
    studyDate: "2026-03-28",
    accession: "RAD-00912-26",
    modality: "MR",
    bodyPart: "LUMBAR SPINE",
    radiologist: "A. Cho, MD",
    radiologistTitle: "Board-Certified Radiologist · Stanford Radiology",
    facility: "Stanford Radiology",
    department: "Neuro & Spine Imaging",
    finding: "Grade II spondylolisthesis L4-L5 with bilateral foraminal stenosis",
    impression:
      "Grade II anterolisthesis at L4-L5 with bilateral foraminal stenosis and moderate central canal narrowing. No focal cord signal abnormality.",
    indication: "Progressive claudication with motor deficit; candidate for fusion.",
    technique: "Sagittal T1 / T2, axial T2, axial PD of the lumbar spine at 1.5T.",
    comparison: "None available.",
    findings: [
      "L4-L5: grade II anterolisthesis with bilateral foraminal narrowing.",
      "Moderate central canal narrowing at L4-L5.",
      "No focal cord or conus signal abnormality.",
      "Paraspinal musculature intact.",
    ],
    series: [
      {
        id: "s1",
        view: "Sagittal T2",
        slice: "10/24",
        orientationL: "A",
        orientationR: "P",
        image: "/demo/knee-mri-sagittal.jpg",
        modalityTag: "MR",
        credit: "Image: Ptrump16, CC BY-SA 4.0 (illustrative)",
        creditUrl: "https://commons.wikimedia.org/wiki/File:Knee_MRI_PD_TSE_FS_Sagittal.jpg",
      },
    ],
  },
};

/* ──────────────────────────────────────────────────────────────
   Integrations mock
   ────────────────────────────────────────────────────────────── */
export const INTEGRATIONS = {
  ehr: [
    {
      id: "epic",
      name: "Epic · FHIR R4",
      status: "connected",
      description: "Live FHIR read scope against the Epic tenant.",
      endpoint: "fhir.stanfordhealth.epic.com/api/FHIR/R4",
      scopes: ["patient/Claim.read", "patient/ExplanationOfBenefit.read", "patient/DocumentReference.read"],
      lastSync: "2 min ago",
      metrics: [
        { label: "Claims synced (24h)", value: "1,284" },
        { label: "Denials ingested (24h)", value: "72" },
      ],
    },
    {
      id: "smart",
      name: "SMART on FHIR launch",
      status: "connected",
      description: "Embedded launch inside the Epic sidebar for physicians.",
      endpoint: "app.panelzero.com/smart/launch",
      scopes: ["launch", "openid", "fhirUser", "patient/*.read"],
      lastSync: "Active — 3 physicians launched today",
    },
  ],
  claims: [
    {
      id: "edi835",
      name: "X12 835 Remittance · SFTP",
      status: "connected",
      description: "Nightly 835 drop from Availity clearinghouse.",
      endpoint: "sftp.availity.com/panelzero/stanfordhealth/in/",
      lastSync: "Last file: 2026-04-15 06:02 UTC (1,248 claims)",
    },
    {
      id: "edi837",
      name: "X12 837 Claim Submission",
      status: "connected",
      description: "Outbound 837 replay for corrected claims.",
      endpoint: "sftp.availity.com/panelzero/stanfordhealth/out/",
      lastSync: "3 files queued — 2026-04-15 23:00 UTC",
    },
  ],
  payer: [
    { id: "availity", name: "Availity Essentials", status: "connected",  description: "Portal submission + status lookups", lastSync: "11 min ago", metrics: [{ label: "Open case #s", value: "38" }] },
    { id: "waystar",  name: "Waystar Clearinghouse", status: "connected", description: "Eligibility and 277CA responses",   lastSync: "24 min ago" },
    { id: "optum",    name: "OptumRx Provider Portal", status: "connected", description: "Pharmacy prior auth + appeals",    lastSync: "1 hr ago" },
    { id: "navinet",  name: "NaviNet Open", status: "attention", description: "MFA rotation required by 2026-04-25", lastSync: "1 day ago" },
  ],
  fax: [
    {
      id: "documo",
      name: "Documo Fax Gateway",
      status: "connected",
      description: "Outbound secure fax delivery with DLR.",
      endpoint: "api.documo.com/v1",
      lastSync: "47 appeals sent in last 7 days · 96% DLR success",
    },
  ],
  voice: [
    {
      id: "voice-ai",
      name: "PanelZero Voice Agent · Twilio",
      status: "connected",
      description: "Automated peer-to-peer scheduling + payer IVR navigation.",
      endpoint: "voice.panelzero.com",
      lastSync: "8 active calls this week · avg hold time 11:42",
    },
  ],
  webhooks: [
    { event: "denials.new",             url: "https://stanfordhealth.example/pz/denials",    status: "ok", last: "5 min ago" },
    { event: "appeals.submitted",       url: "https://stanfordhealth.example/pz/appeals",    status: "ok", last: "17 min ago" },
    { event: "appeals.overturned",      url: "https://stanfordhealth.example/pz/appeals/win",status: "ok", last: "3 hr ago" },
    { event: "appeals.upheld",          url: "https://stanfordhealth.example/pz/appeals/lose",status: "ok", last: "1 day ago" },
  ],
  workflows: [
    { id: "wf-8821", name: "appeal-lifecycle", arg: "claim CH-00293418", status: "running", step: "awaiting-payer-response", eta: "14d 03h" },
    { id: "wf-8823", name: "appeal-lifecycle", arg: "claim CH-00293420", status: "completed", step: "overturned",           eta: "—" },
    { id: "wf-8824", name: "p2p-scheduler",    arg: "claim CH-00293421", status: "running", step: "booking-p2p-slot",     eta: "pending" },
    { id: "wf-8826", name: "fax-retry",        arg: "appeal A-48201",     status: "running", step: "retry-2-of-3",         eta: "06:00" },
  ],
};

/* ──────────────────────────────────────────────────────────────
   Settings mock
   ────────────────────────────────────────────────────────────── */
export const SETTINGS = {
  team: [
    { name: "Yvonne Chen",      role: "Admin",         email: "ychen@stanfordhealth.org",     active: "now"   },
    { name: "Jordan Romero",    role: "Reviewer",      email: "jromero@stanfordhealth.org",   active: "2h ago" },
    { name: "Rhea Ellis",       role: "Reviewer",      email: "rellis@stanfordhealth.org",    active: "6h ago" },
    { name: "Marcus Patel",     role: "Physician · P2P", email: "mpatel@stanfordhealth.org",  active: "1d ago" },
    { name: "Viewer · Finance", role: "Viewer",        email: "finance-ro@stanfordhealth.org", active: "7d ago" },
  ],
  autoSubmit: [
    { id: "co197", label: "CO-197 · Prior auth absent",        threshold: 0.85, enabled: true  },
    { id: "co16",  label: "CO-16  · Claim lacks info",         threshold: 0.80, enabled: true  },
    { id: "n115",  label: "N115   · LCD / policy mismatch",    threshold: 0.82, enabled: true  },
    { id: "co50",  label: "CO-50  · Not medically necessary",  threshold: 0.90, enabled: false },
    { id: "pr204", label: "PR-204 · Non-covered benefit",      threshold: 0.88, enabled: true  },
  ],
  notifications: [
    { channel: "Slack #rev-cycle",   events: ["overturned", "upheld", "needs-review"], enabled: true },
    { channel: "Email · billing-leads@", events: ["daily-digest"], enabled: true },
    { channel: "Webhook · denials.new", events: ["denials.new"], enabled: true },
  ],
  templates: [
    { payer: "Anthem BCBS",          status: "enabled",  version: "2026.03" },
    { payer: "UnitedHealthcare",     status: "enabled",  version: "2026.03" },
    { payer: "Aetna",                status: "enabled",  version: "2026.02" },
    { payer: "Cigna",                status: "enabled",  version: "2026.03" },
    { payer: "Humana",               status: "enabled",  version: "2026.02" },
    { payer: "Kaiser Permanente",    status: "beta",     version: "2026.01" },
    { payer: "BCBS Massachusetts",   status: "enabled",  version: "2026.03" },
    { payer: "Medicare FFS",         status: "enabled",  version: "2026.03" },
  ],
  tone: {
    style: "formal-clinical",
    length: "concise",
    citePolicies: true,
    includeCPT: true,
  },
};
